// jsPDF is loaded on demand (dynamic import) so the heavy PDF library stays out
// of the initial bundle and is only fetched when a PDF is actually created.

// Ensure a data URL is a raster PNG (converts SVG data URLs via canvas).
export function ensurePng(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image/svg')) { resolve(dataUrl); return; }
    const img = new Image();
    img.onload = () => {
      const scale = 3;
      const w = (img.naturalWidth || 300) * scale;
      const h = (img.naturalHeight || 150) * scale;
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      try { resolve(canvas.toDataURL('image/png')); } catch (e) { resolve(dataUrl); }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function loadImageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 100, h: img.naturalHeight || 100 });
    img.onerror = () => resolve({ w: 100, h: 100 });
    img.src = dataUrl;
  });
}

/**
 * Build a print-ready A4 PDF of label cells and save it.
 * labels: [{ data_url, caption, value }]  (already expanded by quantity)
 * opts: { cols, rows, margin, gap, showBorder, showCaption, filename }
 */
export async function generateLabelPdf(labels, opts = {}) {
  const {
    cols = 3, rows = 8, margin = 8, gap = 2,
    showBorder = true, showCaption = true,
    filename = 'etiketten_a4.pdf',
    exact = null,
    offsetX = 0,
    offsetY = 0,
  } = opts;
  if (!labels || !labels.length) return;

  const PW = 210, PH = 297; // A4 mm
  // Exact mode: use the millimetre geometry straight from the gLabels template
  // (label size + offsets + pitch) for pixel-accurate placement on the sheet.
  const useExact = !!(exact && exact.w && exact.h && exact.dx && exact.dy);
  const gCols = useExact ? (exact.cols || cols) : cols;
  const gRows = useExact ? (exact.rows || rows) : rows;
  const usableW = PW - 2 * margin;
  const usableH = PH - 2 * margin;
  const cellW = useExact ? exact.w : (usableW - (cols - 1) * gap) / cols;
  const cellH = useExact ? exact.h : (usableH - (rows - 1) * gap) / rows;
  const perPage = gCols * gRows;
  const pad = 1.5;
  const captionH = showCaption ? 4 : 0;

  // Preload unique image sizes (skip empty start slots)
  const uniqueUrls = Array.from(new Set(labels.filter(Boolean).map((l) => l.data_url)));
  const sizeMap = {};
  await Promise.all(uniqueUrls.map(async (u) => { sizeMap[u] = await loadImageSize(u); }));

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  doc.setFont('helvetica', 'normal');

  labels.forEach((l, i) => {
    const within = i % perPage;
    if (i > 0 && within === 0) doc.addPage();
    const r = Math.floor(within / gCols);
    const c = within % gCols;
    const x = (useExact ? (exact.x0 + c * exact.dx) : (margin + c * (cellW + gap))) + offsetX;
    const y = (useExact ? (exact.y0 + r * exact.dy) : (margin + r * (cellH + gap))) + offsetY;

    if (!l) return; // empty start slot on partially-used sheet

    if (showBorder) {
      doc.setDrawColor(148, 163, 184);
      doc.setLineWidth(0.15);
      if (doc.setLineDashPattern) doc.setLineDashPattern([0.8, 0.6], 0);
      doc.rect(x, y, cellW, cellH);
      if (doc.setLineDashPattern) doc.setLineDashPattern([], 0);
    }

    const size = sizeMap[l.data_url] || { w: 100, h: 100 };
    const areaW = cellW - 2 * pad;
    const areaH = cellH - 2 * pad - captionH;
    const scale = Math.min(areaW / size.w, areaH / size.h);
    const drawW = size.w * scale;
    const drawH = size.h * scale;
    const imgX = x + (cellW - drawW) / 2;
    const imgY = y + pad + (areaH - drawH) / 2;
    try {
      doc.addImage(l.data_url, 'PNG', imgX, imgY, drawW, drawH, undefined, 'FAST');
    } catch (e) { console.error('Failed to add label image to PDF:', e); }

    if (showCaption) {
      const text = String(l.caption || l.value || '');
      if (text) {
        doc.setFontSize(7);
        doc.setTextColor(15, 23, 42);
        const maxChars = Math.max(6, Math.floor(cellW / 1.6));
        const shown = text.length > maxChars ? text.slice(0, maxChars - 1) + '…' : text;
        doc.text(shown, x + cellW / 2, y + cellH - pad - 0.5, { align: 'center', baseline: 'bottom' });
      }
    }
  });

  doc.save(filename);
}

/**
 * Save a single barcode as a centered A4 PDF (with optional caption).
 * dataUrl must be a PNG data URL.
 */
export async function generateSingleBarcodePdf(dataUrl, { caption = '', filename = 'barcode.pdf' } = {}) {
  if (!dataUrl) return;
  const size = await loadImageSize(dataUrl);
  const PW = 210, PH = 297;
  const maxW = 150, maxH = 170;
  const scale = Math.min(maxW / size.w, maxH / size.h);
  const drawW = size.w * scale;
  const drawH = size.h * scale;
  const x = (PW - drawW) / 2;
  const y = (PH - drawH) / 2 - 8;

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  doc.setFont('helvetica', 'normal');
  try {
    doc.addImage(dataUrl, 'PNG', x, y, drawW, drawH, undefined, 'FAST');
  } catch (e) { console.error('Failed to add barcode image to PDF:', e); }
  if (caption) {
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    const shown = String(caption).length > 80 ? String(caption).slice(0, 79) + '…' : String(caption);
    doc.text(shown, PW / 2, y + drawH + 10, { align: 'center' });
  }
  doc.save(filename);
}
